import { v4 as uuidv4 } from "uuid";
import {
  MetricConstants,
  MetricTypeConstants,
} from "../../components/Constants";
import config from "../../components/helpers/socket/config";
import { send } from "../sockets/asyncSocket";

/**
 * Starts recording a metric by storing its start time in localStorage.
 * @param {string} name - The name of the metric.
 * @param {string} type - The type of the metric (e.g., "timer", "counter").
 * @param {object} [tags] - Optional tags for the metric (key-value pairs).
 */
export function startMetric(name, type, tags = {}) {
  const startTime = Date.now();
  const metricData = {
    startTime,
    type,
    tags,
    originalName: name,
  };
  const key = tags.reqId ? `${name}_${tags.reqId}` : name;
  localStorage.setItem(`metric_${key}`, JSON.stringify(metricData));
}

/**
 * Ends recording a metric, calculates the duration (if it's a timer),
 * and sends the metric data to the server.
 * @param {string} name - The name of the metric.
 * @param {object} [tags] - Optional tags for the metric (key-value pairs).
 */
export function endMetric(name, tags = {}) {
  const baseName = name.replace(/\.(success|failure)$/, "");
  const key = tags.reqId ? `${baseName}_${tags.reqId}` : baseName;
  const metricKey = `metric_${key}`;
  const metricDataString = localStorage.getItem(metricKey);

  if (!metricDataString) {
    console.warn(`Metric "${name}" not started for key "${metricKey}".`);
    return;
  }

  localStorage.removeItem(metricKey); // Remove from localStorage

  const metricData = JSON.parse(metricDataString);
  const endTime = Date.now();

  let payload = {
    type: metricData.type,
    name: name,
    tags: { ...metricData.tags, ...tags },
  };

  if (metricData.type === MetricTypeConstants.TIMER) {
    const duration = endTime - metricData.startTime;
    payload.unit = "milliseconds";
    payload.duration = duration;
  } else if (metricData.type === MetricTypeConstants.HISTOGRAM) {
    const duration = endTime - metricData.startTime;
    payload.unit = "milliseconds";
    payload.duration = duration;
  } else if (metricData.type === MetricTypeConstants.COUNTER) {
    // For counters, we just increment by 1 (or you could add a value in startMetric)
    // The server-side logic will handle the actual increment.
    // If you want to support incrementing by values other than 1, you'd need to
    // store that value in startMetric and include it here.
  } else if (metricData.type === MetricTypeConstants.GAUGE) {
    // For gauges, you'd need to pass the current value to endMetric
    console.warn(
      `Gauge metrics require a value to be passed to endMetric.  Metric "${name}" not sent.`,
    );
    return;
  } else {
    console.warn(
      `Unsupported metric type: ${metricData.type}. Metric "${name}" not sent.`,
    );
    return;
  }

  send(config.socket.channels.CORE, {
    action: "sendMetric",
    requestID: uuidv4(), // Assuming you have a uuidv4 function
    type: "notUsedButMustBePresentForLegacyValidation",
    payload: payload,
  });
}

export function remapMetric(baseName, oldReqId, newReqId) {
  const oldKey = `metric_${baseName}_${oldReqId}`;
  const metricDataString = localStorage.getItem(oldKey);

  if (!metricDataString) {
    console.warn(
      `Cannot remap. Metric "${baseName}" not started for key "${oldKey}".`,
    );
    return;
  }

  localStorage.removeItem(oldKey);

  const newKey = `metric_${baseName}_${newReqId}`;
  localStorage.setItem(newKey, metricDataString);
}

export function cancelAllMetrics() {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("metric_")) {
      localStorage.removeItem(key);
    }
  });
}
